'use client';

import { FC } from 'react';
import Image from 'next/image';
import { Box, Button, IconButton, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { FormAutocomplete } from '@/components/common/FormComponents';
import { IdLabelPair } from '@/types/common';
import type { SpecimenRowState } from '@/lib/addToInventoryVaried';

interface AddToInventorySpecimenRowProps {
  /** 0-based position, shown as a 1-based "Specimen N" heading. */
  index: number;
  row: SpecimenRowState;
  /** Patch a subset of this row's fields (keyed by stable row id). */
  onChange: (id: number, patch: Partial<SpecimenRowState>) => void;
  onRemove: (id: number) => void;
  /** Removal is disabled for the last remaining row. */
  canRemove: boolean;
  /** Highlight the material field when submit failed because it is empty. */
  materialError: boolean;
  materialOptions: IdLabelPair[];
  substrateNameOptions: string[];
  vendorOptions: string[];
  lotOptions: string[];
  orientationOptions: string[];
  sizeOptions: string[];
  thicknessOptions: string[];
  holderOptions: string[];
  qualityOptions: string[];
  onInteract: () => void;
}

/** Filters out blank/whitespace-only dropdown entries (substrate choice lists can contain empties). */
const nonEmpty = (option: string) => Boolean(option) && option.trim() !== '';

/**
 * One specimen in "Customize each" add-to-inventory: substrate fields (matching the Same-mode substrate block)
 * plus an optional per-row image. State lives in the parent; this component is presentational and reports changes
 * through {@link AddToInventorySpecimenRowProps.onChange}.
 */
export const AddToInventorySpecimenRow: FC<AddToInventorySpecimenRowProps> = ({
  index,
  row,
  onChange,
  onRemove,
  canRemove,
  materialError,
  materialOptions,
  substrateNameOptions,
  vendorOptions,
  lotOptions,
  orientationOptions,
  sizeOptions,
  thicknessOptions,
  holderOptions,
  qualityOptions,
  onInteract,
}) => {
  // Single change helper so every field both updates state and marks the form as interacted.
  const patch = (next: Partial<SpecimenRowState>) => {
    onChange(row.id, next);
    onInteract();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      patch({ file });
    }
    // Allow re-selecting the same file after a remove by clearing the input value.
    event.target.value = '';
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" fontWeight={600}>
          Specimen {index + 1}
        </Typography>
        <IconButton
          size="small"
          color="error"
          onClick={() => onRemove(row.id)}
          disabled={!canRemove}
          title={canRemove ? 'Remove specimen' : 'At least one specimen is required'}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <FormAutocomplete
        freeSolo
        options={substrateNameOptions.filter(nonEmpty)}
        value={row.substrateName || null}
        onChange={(_, newValue) => patch({ substrateName: (typeof newValue === 'string' ? newValue : '') || '' })}
        onInputChange={(_, newInputValue) => patch({ substrateName: newInputValue || '' })}
        label="Substrate name"
        size="small"
        fullWidth
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <FormAutocomplete
          options={materialOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
          isOptionEqualToValue={(option, value) => (option as IdLabelPair).id === (value as IdLabelPair)?.id}
          value={row.materialIds[0] != null ? (materialOptions.find((m) => m.id === row.materialIds[0]) ?? null) : null}
          onChange={(_, newValue) => patch({ materialIds: newValue ? [(newValue as IdLabelPair).id] : [] })}
          autoSelect
          autoHighlight
          renderOption={(props, option) => (
            <li {...props} key={(option as IdLabelPair).id}>
              {(option as IdLabelPair).label}
            </li>
          )}
          label="Material"
          required
          error={materialError}
          helperText={materialError ? 'Material is required.' : undefined}
          size="small"
          fullWidth
        />
        <FormAutocomplete
          freeSolo
          options={lotOptions.filter(nonEmpty)}
          value={row.lot || null}
          onChange={(_, newValue) => patch({ lot: (newValue as string) || '' })}
          onInputChange={(_, newInputValue) => patch({ lot: newInputValue || '' })}
          label="Lot"
          size="small"
          fullWidth
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <FormAutocomplete
          freeSolo
          options={vendorOptions.filter(nonEmpty)}
          value={row.vendor || null}
          onChange={(_, newValue) => patch({ vendor: (newValue as string) || '' })}
          onInputChange={(_, newInputValue) => patch({ vendor: newInputValue || '' })}
          label="Vendor"
          size="small"
          fullWidth
        />
        <FormAutocomplete
          freeSolo
          options={orientationOptions.filter(nonEmpty)}
          value={row.orientation || null}
          onChange={(_, newValue) => patch({ orientation: (newValue as string) || '' })}
          onInputChange={(_, newInputValue) => patch({ orientation: newInputValue || '' })}
          label="Orientation"
          size="small"
          fullWidth
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 1.5,
          alignItems: 'start',
        }}
      >
        <FormAutocomplete
          freeSolo
          options={sizeOptions.filter(nonEmpty)}
          value={row.size || null}
          onChange={(_, newValue) => patch({ size: (newValue as string) || '' })}
          onInputChange={(_, newInputValue) => patch({ size: newInputValue || '' })}
          label="Size"
          size="small"
          fullWidth
        />
        <FormAutocomplete
          freeSolo
          options={thicknessOptions.filter(nonEmpty)}
          value={row.thickness || null}
          onChange={(_, newValue) => patch({ thickness: (newValue as string) || '' })}
          onInputChange={(_, newInputValue) => patch({ thickness: newInputValue || '' })}
          label="Thickness"
          size="small"
          fullWidth
        />
        <FormAutocomplete
          freeSolo
          options={holderOptions.filter(nonEmpty)}
          value={row.holder || null}
          onChange={(_, newValue) => patch({ holder: (newValue as string) || '' })}
          onInputChange={(_, newInputValue) => patch({ holder: newInputValue || '' })}
          label="Holder"
          size="small"
          fullWidth
        />
        <FormAutocomplete
          freeSolo
          options={qualityOptions.filter(nonEmpty)}
          value={row.quality || null}
          onChange={(_, newValue) => patch({ quality: (newValue as string) || '' })}
          onInputChange={(_, newInputValue) => patch({ quality: newInputValue || '' })}
          label="Quality"
          size="small"
          fullWidth
        />
      </Box>

      {/* Per-row image (optional) — independent of the Same-mode single image. */}
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<AttachFileIcon />}
          component="label"
          sx={{ whiteSpace: 'nowrap' }}
        >
          Attach Image
          <input type="file" hidden accept="image/*" onChange={handleFileChange} />
        </Button>
        {row.file ? (
          <Box display="flex" gap={1} alignItems="center">
            <Box sx={{ position: 'relative', width: 40, height: 40, borderRadius: 1, overflow: 'hidden' }}>
              <Image src={URL.createObjectURL(row.file)} alt="Preview" layout="fill" objectFit="cover" unoptimized />
            </Box>
            <Typography variant="caption" sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}>
              {row.file.name}
            </Typography>
            <IconButton size="small" color="error" onClick={() => patch({ file: null })} title="Remove image">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
